// NEW FILE: src/components/NotificationHistory.js
// Component for displaying notification history of ticket sales

import React, { useState, useEffect } from 'react';
import {
  History,
  Bell,
  Ticket,
  Clock,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { formatTime } from '../utils/helpers';

const NotificationHistory = ({ notifications = [], onClearHistory }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const notificationsPerPage = 10;

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showHistory && !event.target.closest('[data-notification-dropdown]')) {
        setShowHistory(false);
      }
    };

    if (showHistory) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showHistory]);

  // Reset to first page when dropdown is opened
  useEffect(() => {
    if (showHistory) {
      setCurrentPage(1);
    }
  }, [showHistory]);

  const hasNotifications = notifications.length > 0;

  // Pagination calculations
  const totalPages = Math.ceil(notifications.length / notificationsPerPage);
  const startIndex = (currentPage - 1) * notificationsPerPage;
  const endIndex = startIndex + notificationsPerPage;
  const currentNotifications = notifications.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="relative" data-notification-dropdown>
      <button
        onClick={() => setShowHistory(!showHistory)}
        className="flex items-center gap-1 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative"
        title="Notification History"
      >
        <History className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        
        {/* Notification count badge */}
        {hasNotifications && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs rounded-full flex items-center justify-center leading-none">
            {notifications.length > 99 ? '99+' : notifications.length}
          </span>
        )}
        
        <span className="text-xs text-gray-500 dark:text-gray-400 hidden lg:inline">
          {hasNotifications ? `${notifications.length} alert${notifications.length > 1 ? 's' : ''}` : 'No alerts'}
        </span>
      </button>

      {/* Notification History Dropdown */}
      {showHistory && (
        <div className="absolute mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-96 flex flex-col" style={{ right: '-20px', width: 'min(calc(100vw - 2rem), 384px)', maxWidth: '100vw', zIndex: 9999 }}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                Notification History
              </h3>
            </div>
            
            {hasNotifications && (
              <button
                onClick={() => {
                  onClearHistory?.();
                  setShowHistory(false);
                }}
                className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                title="Clear all notifications"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {hasNotifications ? (
              <div className="space-y-0">
                {currentNotifications.map((notification, index) => {
                  // Treat missing `type` as legacy sale for backward compat with old localStorage entries.
                  const isStateChange = notification.type === 'state_change';
                  const iconBg = isStateChange
                    ? 'bg-red-100 dark:bg-red-900/30'
                    : 'bg-green-100 dark:bg-green-900/30';
                  const iconColor = isStateChange
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400';
                  const Icon = isStateChange ? AlertCircle : Ticket;
                  return (
                  <div
                    key={`${notification.timestamp}-${index}`}
                    className="p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className={`w-8 h-8 ${iconBg} rounded-full flex items-center justify-center`}>
                          <Icon className={`w-4 h-4 ${iconColor}`} />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {notification.eventTitle}
                            </p>
                            {isStateChange ? (
                              <p className="text-sm text-red-600 dark:text-red-400 mt-0.5 font-medium">
                                {notification.summary || (() => {
                                  const parts = [];
                                  if (notification.counts?.disputed) parts.push(`${notification.counts.disputed} disputed`);
                                  if (notification.counts?.voided)   parts.push(`${notification.counts.voided} voided`);
                                  if (notification.counts?.refunded) parts.push(`${notification.counts.refunded} refunded`);
                                  return parts.join(', ');
                                })()}
                              </p>
                            ) : (
                              <p className="text-sm text-green-600 dark:text-green-400 mt-0.5">
                                +{notification.ticketsSold} ticket{notification.ticketsSold > 1 ? 's' : ''} sold
                                {notification.revenue > 0 && (
                                  <span className="ml-2 font-medium">
                                    ${notification.revenue.toLocaleString()}
                                  </span>
                                )}
                                {notification.revenue === 0 && (
                                  // $0 sale = free-tier claim: say it, never fake a price
                                  <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                                    Free
                                  </span>
                                )}
                              </p>
                            )}
                            {!isStateChange && notification.details && Object.keys(notification.details).some(key => notification.details[key] > 0) && (
                              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                {Object.entries(notification.details)
                                  .filter(([_, count]) => count > 0)
                                  .map(([category, count]) => {
                                    // Format category names to be more readable
                                    const formattedCategory = category
                                      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                                      .replace(/^rsvp Early /i, 'RSVP ') // "rsvp Early Female" → "RSVP Female"
                                      .replace(/^early /, 'Early ') // Capitalize "early"
                                      .replace(/^general /, 'General ') // Capitalize "general"
                                      .replace(/^vip/, 'VIP') // Uppercase VIP
                                      .replace(/^comp/, 'Comp') // Capitalize Comp
                                      .trim();
                                    return `${formattedCategory}: +${count}`;
                                  })
                                  .join(', ')}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                            <Clock className="w-3 h-3" />
                            {formatTime(new Date(notification.timestamp))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        Showing {startIndex + 1}-{Math.min(endIndex, notifications.length)} of {notifications.length}
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={goToPrevPage}
                          disabled={currentPage === 1}
                          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                          title="Previous page"
                        >
                          <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>

                        <span className="text-xs text-gray-600 dark:text-gray-400 px-2">
                          Page {currentPage} of {totalPages}
                        </span>

                        <button
                          onClick={goToNextPage}
                          disabled={currentPage === totalPages}
                          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                          title="Next page"
                        >
                          <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="text-gray-400 dark:text-gray-600 mb-3">
                  <AlertCircle className="w-8 h-8 mx-auto" />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">No notifications yet</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Ticket sale alerts will appear here
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          {hasNotifications && (
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-lg">
              <div className="text-xs text-gray-600 dark:text-gray-400 text-center">
                <div>
                  History saved • Last {notifications.length > 1 ? `${notifications.length} sales` : 'sale'}
                  {notifications.length >= 100 && ' (showing last 100)'}
                </div>
                <div className="mt-1">
                  Total tracked: {notifications.reduce((sum, n) => sum + n.ticketsSold, 0)} tickets
                  {notifications.some(n => n.revenue > 0) && (
                    <span className="ml-2">
                      • ${notifications.reduce((sum, n) => sum + (n.revenue || 0), 0).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationHistory;